import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Tooltip, CircularProgress, Alert,
  useTheme, alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  RateReview as ReviewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import wineRegionService, { WineRegion } from '../../../services/wineRegionService';
import { getCountryName } from '../../../utils/countryUtils';
import RegionSearchBox from '../../../components/RegionSearchBox';
import RegionGrapeRulesPanel from '../../../components/wine/RegionGrapeRulesPanel';

const countryFlag = (code: string) =>
  code.toUpperCase().replace(/./g, c => String.fromCodePoint(c.charCodeAt(0) + 127397));

const RegionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { regionId } = useParams<{ regionId: string }>();
  const theme = useTheme();

  // ── region data ───────────────────────────────────────────────────────────
  const [region, setRegion] = useState<WineRegion | null>(null);
  const [loadingRegion, setLoadingRegion] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // ── sub-regions ───────────────────────────────────────────────────────────
  const [subRegions, setSubRegions] = useState<WineRegion[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    totalItems: 0,
  });

  // ── derived parent info ───────────────────────────────────────────────────
  const parentId = region?.hierarchyPath && region.hierarchyPath.length > 1
    ? region.hierarchyPath[region.hierarchyPath.length - 2]
    : null;
  const parentName = region?.hierarchyNames && region.hierarchyNames.length > 1
    ? region.hierarchyNames[region.hierarchyNames.length - 2]
    : null;
  const parentDisplayName = parentName && /^[A-Z]{2}$/.test(parentName)
    ? getCountryName(parentName)
    : parentName;

  const backPath = parentId ? `/admin/regions/${parentId}` : '/admin/regions';
  const backLabel = parentDisplayName ?? 'All Regions';

  // ── load region ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!regionId) return;
    setLoadingRegion(true);
    setLoadError(false);
    wineRegionService.getWineRegion(regionId)
      .then(setRegion)
      .catch(() => setLoadError(true))
      .finally(() => setLoadingRegion(false));
  }, [regionId]);

  // ── load sub-regions ──────────────────────────────────────────────────────
  const fetchChildren = useCallback(() => {
    if (!regionId) return;
    setLoadingChildren(true);
    wineRegionService
      .getWineRegionChildren(regionId, pagination.page + 1, pagination.pageSize)
      .then(resp => {
        setSubRegions(resp.data);
        setPagination(prev => ({ ...prev, totalItems: resp.pagination.totalItems }));
      })
      .catch(() => setSubRegions([]))
      .finally(() => setLoadingChildren(false));
  }, [regionId, pagination.page, pagination.pageSize]);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);

  // ── actions ───────────────────────────────────────────────────────────────
  const handleDeleteRegion = async () => {
    if (!region) return;
    const displayName = /^[A-Z]{2}$/.test(region.name) ? getCountryName(region.name) : region.name;
    if (!window.confirm(`Delete "${displayName}"? This cannot be undone.`)) return;
    try {
      await wineRegionService.deleteWineRegion(region._id);
      navigate(backPath);
    } catch {
      alert('Failed to delete. This region may have sub-regions or be referenced by wines.');
    }
  };

  const handleDeleteChild = async (child: WineRegion, e: React.MouseEvent) => {
    e.stopPropagation();
    const displayName = /^[A-Z]{2}$/.test(child.name) ? getCountryName(child.name) : child.name;
    if (!window.confirm(`Delete "${displayName}"?`)) return;
    try {
      await wineRegionService.deleteWineRegion(child._id);
      fetchChildren();
    } catch {
      alert('Failed to delete. This region may have sub-regions or be referenced by wines.');
    }
  };

  // ── display helpers ───────────────────────────────────────────────────────
  const displayName = (r: WineRegion) =>
    /^[A-Z]{2}$/.test(r.name) ? getCountryName(r.name) : r.name;

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  // ── loading / error states ────────────────────────────────────────────────
  if (loadingRegion) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !region) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start' }}>
          {backLabel}
        </Button>
        <Alert severity="error">Could not load this region. It may have been deleted.</Alert>
      </Box>
    );
  }

  const paperSx = {
    elevation: 0,
    borderRadius: 2,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(10px)',
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Back button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate(backPath)}
        sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start', pl: 0 }}
      >
        {backLabel}
      </Button>

      {/* Search */}
      <RegionSearchBox
        onSearch={q => navigate(`/admin/regions?search=${encodeURIComponent(q)}`)}
        onSelectRegion={id => navigate(`/admin/regions/${id}`)}
        fullWidth
        size="medium"
      />

      {/* ── Region identity card ─────────────────────────────────────────── */}
      <Paper sx={paperSx}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          {/* Left — name + hierarchy */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {region.country && (
                <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                  {countryFlag(region.country)}
                </Typography>
              )}
              <Typography variant="h5" fontWeight={700}>
                {displayName(region)}
              </Typography>
              {region.needsReview && (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                  label="Needs Review"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>

            {region.localName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {region.localName}
              </Typography>
            )}

            {region.hierarchyNames && region.hierarchyNames.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                {region.hierarchyNames.map((seg, i) => {
                  const label = /^[A-Z]{2}$/.test(seg) ? getCountryName(seg) : seg;
                  const id = region.hierarchyPath?.[i];
                  const isLast = i === region.hierarchyNames!.length - 1;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && <span style={{ margin: '0 4px', opacity: 0.5 }}>›</span>}
                      {isLast ? (
                        <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{label}</span>
                      ) : id ? (
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/admin/regions/${id}`)}
                        >
                          {label}
                        </span>
                      ) : label}
                    </React.Fragment>
                  );
                })}
              </Typography>
            )}
          </Box>

          {/* Right — actions */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/regions/${region._id}/edit`)}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
            <Tooltip title="Delete region">
              <IconButton color="error" onClick={handleDeleteRegion}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Metadata strip */}
        <Box sx={{
          px: 3, py: 1.5,
          display: 'flex', gap: 4, flexWrap: 'wrap',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: alpha(theme.palette.grey[50] ?? '#fafafa', 0.6),
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}>
          {region.country && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
                Country
              </Typography>
              <Typography variant="body2">
                {getCountryName(region.country)}
              </Typography>
            </Box>
          )}
          {region.level !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
                Level
              </Typography>
              <Typography variant="body2">{region.level}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
              Created
            </Typography>
            <Typography variant="body2">{formatDate(region.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
              Updated
            </Typography>
            <Typography variant="body2">{formatDate(region.updatedAt)}</Typography>
          </Box>
        </Box>

      </Paper>

      {/* ── GK-1: what this region permits ───────────────────────────────── */}
      {regionId && <RegionGrapeRulesPanel regionId={regionId} regionName={region.name} />}

      {/* ── Sub-regions ──────────────────────────────────────────────────── */}
      <Paper sx={paperSx}>
        {/* Sub-regions header */}
        <Box sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>Sub-regions</Typography>
            {!loadingChildren && (
              <Chip label={pagination.totalItems} size="small" sx={{ fontWeight: 600 }} />
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/admin/regions/create?parent=${region._id}`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add Sub-region
          </Button>
        </Box>

        {loadingChildren ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : subRegions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No sub-regions yet. Click <strong>Add Sub-region</strong> to create one.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Local Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subRegions.map(child => (
                    <TableRow
                      key={child._id}
                      hover
                      onClick={() => navigate(`/admin/regions/${child._id}`)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: child.needsReview
                          ? alpha(theme.palette.warning.main, 0.06)
                          : 'inherit',
                        '&:hover': {
                          backgroundColor: child.needsReview
                            ? alpha(theme.palette.warning.main, 0.12)
                            : alpha(theme.palette.action.hover, 0.6),
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2">{displayName(child)}</Typography>
                            {child.hierarchyNames && child.hierarchyNames.length > 1 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {child.hierarchyNames.map(s => /^[A-Z]{2}$/.test(s) ? getCountryName(s) : s).join(' › ')}
                              </Typography>
                            )}
                          </Box>
                          {child.needsReview && (
                            <Tooltip title="Needs review">
                              <ReviewIcon fontSize="small" sx={{ color: 'warning.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={child.localName ? 'text.primary' : 'text.disabled'}>
                          {child.localName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={e => { e.stopPropagation(); navigate(`/admin/regions/${child._id}/edit`); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={e => handleDeleteChild(child, e)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
              onRowsPerPageChange={e => setPagination(prev => ({
                ...prev, page: 0, pageSize: parseInt(e.target.value, 10),
              }))}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RegionDetail;
